import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Tooltip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Users, AlertTriangle } from 'lucide-react';

import { bandwidthService, dashboardService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { isAdminOrManager } from '@/shared/utils/roles';
import { formatPercent } from '@/shared/utils/formatPercentage';

/**
 * TeamWorkloadWidget (Manager View)
 * Heatmap visualization of team member workloads
 * Quick identification of overloaded/underloaded team members
 */
const TeamWorkloadWidget: React.FC = () => {
  const user = useAppSelector(selectCurrentUser);
  const teamId = user?.teamId || '';

  const { data: teamBandwidthData, isLoading: teamLoading } = useQuery({
    queryKey: QUERY_KEYS.teamBandwidth(teamId),
    queryFn: () => bandwidthService.getTeamBandwidth(teamId),
    enabled: !!teamId,
    staleTime: 60000,
  });

  const { data: fallbackMembers, isLoading: fallbackLoading } = useQuery({
    queryKey: ['teamMembersWorkload', 'widget-fallback', user?.id],
    queryFn: () => dashboardService.getTeamMembersWithWorkload(),
    enabled: !teamId && isAdminOrManager(user?.role),
    staleTime: 60000,
  });

  const isLoading = teamId ? teamLoading : fallbackLoading;

  const teamBandwidth = teamId
    ? teamBandwidthData?.data
    : fallbackMembers
      ? {
          members: fallbackMembers.map((m) => ({
            userId: m.userId ?? m.id,
            userName: m.name,
            name: m.name,
            utilizationPercentage: m.utilizationPercentage ?? m.utilizationPercent ?? 0,
            allocatedHours: m.allocatedHours ?? m.hoursAllocated ?? 0,
            loggedHours: m.hoursLogged ?? 0,
            totalAvailableHours: m.availableHours ?? 0,
            isOverloaded: (m.utilizationPercentage ?? m.utilizationPercent ?? 0) > 100,
          })),
          overloadedMemberCount: fallbackMembers.filter(
            (m) => (m.utilizationPercentage ?? m.utilizationPercent ?? 0) > 100
          ).length,
        }
      : undefined;
  const members = teamBandwidth?.members || [];

  // Get color for utilization percentage
  const getUtilizationColor = (percent: number) => {
    if (percent > 100) return { bg: '#fef2f2', border: '#fecaca', text: '#dc2626' };
    if (percent > 85) return { bg: '#fffbeb', border: '#fde68a', text: '#d97706' };
    if (percent > 50) return { bg: '#eff6ff', border: '#bfdbfe', text: '#2563eb' };
    return { bg: '#f0fdf4', border: '#bbf7d0', text: '#16a34a' };
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width={180} height={28} />
          <Skeleton variant="rounded" height={200} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Users size={20} style={{ color: '#64748b' }} />
            <Typography variant="h6" fontWeight={600}>
              Team Workload
            </Typography>
          </Box>
          {teamBandwidth && teamBandwidth.overloadedMemberCount > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                bgcolor: 'error.50',
                borderRadius: '12px',
              }}
            >
              <AlertTriangle size={14} style={{ color: '#dc2626' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'error.main' }}>
                {teamBandwidth.overloadedMemberCount} overloaded
              </Typography>
            </Box>
          )}
        </Box>

        {/* Workload Grid */}
        {members.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No team members to display
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {members.map((member) => {
              const colors = getUtilizationColor(member.utilizationPercentage);
              return (
                <Tooltip
                  key={member.userId}
                  title={
                    <Box>
                      <Typography variant="body2">
                        Allocated: {member.allocatedHours.toFixed(1)}h
                      </Typography>
                      <Typography variant="body2">
                        Logged: {member.loggedHours.toFixed(1)}h
                      </Typography>
                      <Typography variant="body2">
                        Available: {member.totalAvailableHours}h
                      </Typography>
                    </Box>
                  }
                >
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: colors.bg,
                      border: '1px solid',
                      borderColor: colors.border,
                      borderRadius: 2,
                      minWidth: 120,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 1,
                      },
                    }}
                  >
                    {/* Avatar placeholder */}
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: colors.text,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 1,
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      }}
                    >
                      {(member.userName || member.name || member.userId).charAt(0).toUpperCase()}
                    </Box>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {member.userName || member.name || member.userId}
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ color: colors.text }}
                    >
                      {formatPercent(member.utilizationPercentage)}
                    </Typography>
                    {member.isOverloaded && (
                      <AlertTriangle
                        size={14}
                        style={{ color: '#dc2626', marginTop: 4 }}
                      />
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        )}

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'center' }}>
          {[
            { label: 'Available', color: '#16a34a' },
            { label: 'Optimal', color: '#2563eb' },
            { label: 'Busy', color: '#d97706' },
            { label: 'Overloaded', color: '#dc2626' },
          ].map((item) => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: item.color,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default TeamWorkloadWidget;
